import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Box, Typography, Paper, Stack, IconButton,
  TextField, InputAdornment, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow,
} from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale, LinearScale,
  BarElement, LineElement, PointElement,
  ArcElement, Filler,
  Tooltip as ChartTooltip, Legend,
} from "chart.js";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import SearchIcon from "@mui/icons-material/Search";
import GroupsIcon from "@mui/icons-material/Groups";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SettingsIcon from "@mui/icons-material/Settings";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { dashBoard_V2 } from "../../store/reducers/LoginReducer";

ChartJS.register(
  CategoryScale, LinearScale,
  BarElement, LineElement, PointElement,
  ArcElement, Filler,
  ChartTooltip, Legend,
);



/* ── Google Fonts ── */
if (!document.head.querySelector("[href*='Plus+Jakarta']")) {
  const l = document.createElement("link");
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap";
  document.head.appendChild(l);
}



/* ══════════════════════════════════════════════════════
   SINGLE MOCK JSON  — replace with API response shape
══════════════════════════════════════════════════════ */
const MOCK = {
  /* ── 4 stat cards ── */
  taskSummaryCardData: [
    {
      title: "Backlog Tasks", count: 23,
      gradient: "linear-gradient(135deg,#f43f5e,#be123c)", ico: "⏳",
      breakdown: [{ label: "Self", value: 4 }, { label: "Manager", value: 5 }, { label: "Sprint", value: 14 }],
    },
    {
      title: "To Do Tasks", count: 1,
      gradient: "linear-gradient(135deg,#06b6d4,#0891b2)", ico: "🔄",
      breakdown: [{ label: "Self", value: 1 }, { label: "Manager", value: 0 }, { label: "Sprint", value: 0 }],
    },
    {
      title: "Completed Tasks", count: 340,
      gradient: "linear-gradient(135deg,#10b981,#059669)", ico: "✅",
      breakdown: [{ label: "Self", value: 326 }, { label: "Manager", value: 10 }, { label: "Sprint", value: 4 }],
    },
    {
      title: "Total Tasks", count: 98,
      gradient: "linear-gradient(135deg,#f59e0b,#d97706)", ico: "💼",
      breakdown: [{ label: "SPA", value: 15 }, { label: "Approved", value: 75 }, { label: "Blocked", value: 8 }],
    },
  ],

  /* ── Sprint list + burndown per sprint ── */
  sprints: [
    {
      id: "SH060", name: "QA Engineer", date: "11-07 to 15-07-2025",
      backlog: 0, todo: 0, completed: 1, approved: 1, total: 2,
      burndown: {
        labels: ["Jul 11", "Jul 12", "Jul 13", "Jul 14", "Jul 15"],
        ideal: [3.2, 2.4, 1.6, 0.8, 0],
        actual: [3.2, 2.8, 1.8, 1.4, 0.8],
      },
    },
    {
      id: "SH062", name: "UI/UX Design", date: "07-07 to 12-07-2025",
      backlog: 0, todo: 0, completed: 1, approved: 0, total: 1,
      burndown: {
        labels: ["Jul 07", "Jul 08", "Jul 09", "Jul 10", "Jul 11", "Jul 12"],
        ideal: [5, 4, 3, 2, 1, 0],
        actual: [5, 4.5, 3.8, 3, 2.2, 1.5],
      },
    },
    {
      id: "SH063", name: "SYS PROJECT", date: "07-07 to 12-07-2025",
      backlog: 0, todo: 0, completed: 8, approved: 0, total: 8,
      burndown: {
        labels: ["Jul 07", "Jul 08", "Jul 09", "Jul 10", "Jul 11", "Jul 12"],
        ideal: [54, 43.2, 32.4, 21.6, 10.8, 0],
        actual: [54, 48, 36, 26, 18, 8],
      },
    },
  ],

  /* ── Sprint Task Summary grouped bar ── */
  sprintTaskSummary: {
    /* labels match sprints[].id */
    labels: ["SH060", "SH062", "SH063"],
    backlog: [0, 0, 0],
    todo: [0, 0, 0],
    completed: [1, 1, 8],
    approved: [1, 0, 0],
  },

  /* ── Task Status donut ── */
  taskStatus: [
    { label: "Backlog", value: 23, color: "#f43f5e" },
    { label: "To Do", value: 1, color: "#06b6d4" },
    { label: "Completed", value: 340, color: "#10b981" },
    { label: "Blocked", value: 8, color: "#8b5cf6" },
    { label: "Approved", value: 75, color: "#f59e0b" },
  ],

  /* ── Team Velocity ── */
  teamVelocity: {
    labels: ["SH058", "SH059", "SH060", "SH061", "SH062", "SH063"],
    planned: [40, 38, 35, 42, 30, 54],
    actual: [38, 40, 28, 44, 25, 46],
  },

  /* ── Cumulative Flow Diagram ── */
  cumulativeFlow: {
    labels: ["Jul 1", "Jul 3", "Jul 5", "Jul 7", "Jul 9", "Jul 11", "Jul 13", "Jul 15"],
    done: [10, 30, 60, 90, 140, 210, 290, 340],
    inprog: [18, 22, 25, 28, 20, 18, 15, 24],
    backlog: [70, 55, 45, 30, 28, 22, 18, 23],
  },

  /* ── Sprint Completion horizontal bars ── */
  sprintCompletion: [
    { label: "SH060 · QA Eng", pct: 50, done: 1, total: 2 },
    { label: "SH062 · UI/UX", pct: 100, done: 1, total: 1 },
    { label: "SH063 · SYS PRJ", pct: 88, done: 8, total: 9 },
  ],

  /* ── Bug / Defect Trend ── */
  bugTrend: {
    labels: ["SH058", "SH059", "SH060", "SH061", "SH062", "SH063"],
    opened: [5, 8, 3, 7, 4, 6],
    closed: [3, 6, 5, 5, 4, 7],
  },

  /* ── Lead & Cycle Time gauges ── */
  leadCycleTime: [
    { label: "Lead Time", value: 4.2, max: 10, unit: "days", color: "#f59e0b", sub: "Backlog → Done" },
    { label: "Cycle Time", value: 2.1, max: 10, unit: "days", color: "#3b82f6", sub: "In Progress → Done" },
  ],

  /* ── Member Workload ── */
  memberWorkload: [
    { name: "Alice", backlog: 2, inprog: 3, done: 8, blocked: 0 },
    { name: "Bob", backlog: 1, inprog: 2, done: 12, blocked: 1 },
    { name: "Carol", backlog: 3, inprog: 4, done: 6, blocked: 2 },
    { name: "David", backlog: 0, inprog: 5, done: 9, blocked: 0 },
    { name: "Eva", backlog: 2, inprog: 1, done: 15, blocked: 1 },
    { name: "Frank", backlog: 4, inprog: 2, done: 7, blocked: 0 },
  ],
};



/* ══════════════════════════════════════════════════════
   THEME TOKENS
══════════════════════════════════════════════════════ */
const DARK = {
  bg: "#0b0f1a", s1: "#111827", s2: "#1c2638",
  bdr: "rgba(255,255,255,.08)", bdr2: "rgba(255,255,255,.15)",
  txt: "#eef2ff", muted: "#7a8db0",
  grid: "rgba(134, 87, 87, 0.04)", axis: "rgba(255,255,255,.1)",
  barBg: "#1c2638", stroke: "#111827",
  rowActive: "rgba(59,130,246,.13)", theadBg: "#1c2638",
};
const LIGHT = {
  bg: "#f1f5fb", s1: "#ffffff", s2: "#f0f4fc",
  bdr: "rgba(0,0,0,.08)", bdr2: "rgba(0,0,0,.14)",
  txt: "#0f172a", muted: "#64748b",
  grid: "rgba(0,0,0,.04)", axis: "rgba(0,0,0,.1)",
  barBg: "#e4eaf6", stroke: "#ffffff",
  rowActive: "rgba(59,130,246,.08)", theadBg: "#f0f4fc",
};

const chartFont = { family: "Plus Jakarta Sans, sans-serif", size: 9 };

/* ══════════════════════════════════════════════════════
   SMALL REUSABLE COMPONENTS
══════════════════════════════════════════════════════ */
const PulsingDot = ({ color }) => (
  <Box component="span" sx={{
    display: "inline-block", width: 7, height: 7, borderRadius: "50%",
    bgcolor: color, boxShadow: `0 0 6px ${color}`, flexShrink: 0,
    animation: "smPulse 2s infinite", overflow: "hidden",
  }} />
);

const LegendDot = ({ color, label, T }) => (
  <Stack direction="row" alignItems="center" spacing={0.6}>
    <Box sx={{ width: 9, height: 9, borderRadius: "2px", bgcolor: color, flexShrink: 0 }} />
    <Typography sx={{ fontSize: 10.5, color: T.muted, fontWeight: 600, fontFamily: "Plus Jakarta Sans,sans-serif" }}>
      {label}
    </Typography>
  </Stack>
);

const PanelHeader = ({ dotColor, title, right, T }) => (
  <Box sx={{
    display: "flex", alignItems: "center", justifyContent: "space-between",
    px: 2, py: "12px", borderBottom: `1px solid ${T.bdr}`, overflow: "hidden",
  }}>
    <Stack direction="row" alignItems="center" spacing={0.8}>
      <PulsingDot color={dotColor} />
      <Typography sx={{
        fontSize: 11, fontWeight: 700, letterSpacing: ".07em",
        textTransform: "uppercase", color: T.txt,
        fontFamily: "Plus Jakarta Sans,sans-serif",
      }}>{title}</Typography>
    </Stack>
    {right && <Box>{right}</Box>}
  </Box>
);

const SectionLabel = ({ children, T }) => (
  <Typography sx={{
    fontSize: 9, fontWeight: 800, letterSpacing: ".15em", textTransform: "uppercase",
    color: T.muted, mb: "10px", pl: "2px", fontFamily: "Plus Jakarta Sans,sans-serif",
  }}>{children}</Typography>
);

const Panel = ({ children, sx = {}, T }) => (
  <Paper elevation={0} sx={{
    bgcolor: T.s1, border: `1px solid ${T.bdr}`, borderRadius: "14px",
    overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.12)",
    transition: "background .3s,border-color .3s", ...sx,
  }}>{children}</Paper>
);

/* ══════════════════════════════════════════════════════
   GAUGE  — SVG semicircle (no chart.js needed here)
══════════════════════════════════════════════════════ */
function Gauge({ item, T }) {
  const pct = item.value / item.max;
  const R = 55, cx = 80, cy = 98, sw = 14;
  const deg = (sd) => {
    const rad = (sd * Math.PI) / 180;
    return { x: cx + R * Math.cos(rad), y: cy + R * Math.sin(rad) };
  };
  const arcD = (start, end) => {
    const s = deg(start), e = deg(end);
    const large = (end - start) > 180 ? 1 : 0;
    return `M${s.x} ${s.y} A${R} ${R} 0 ${large} 1 ${e.x} ${e.y}`;
  };
  const endDeg = 180 + pct * 180;
  return (
    <Box sx={{ textAlign: "center", flex: 1, overflow: "hidden" }}>
      <svg viewBox="0 0 160 120" width="100%" style={{ display: "block", maxWidth: 200, margin: "0 auto" }}>
        {/* track */}
        <path d={arcD(180, 360)} fill="none" stroke={T.barBg} strokeWidth={sw} strokeLinecap="round" />
        {/* value */}
        <path d={arcD(180, endDeg)} fill="none" stroke={item.color} strokeWidth={sw} strokeLinecap="round" />
        {/* min/max labels */}
        <text x={cx - R - 6} y={cy + 6} fill={T.muted} fontSize={8} textAnchor="end"
          fontFamily="Plus Jakarta Sans,sans-serif">0</text>
        <text x={cx + R + 6} y={cy + 6} fill={T.muted} fontSize={8} textAnchor="start"
          fontFamily="Plus Jakarta Sans,sans-serif">{item.max}</text>
        {/* center value */}
        <text x={cx} y={cy - 14} fill={item.color} fontSize={22} textAnchor="middle"
          fontFamily="Plus Jakarta Sans,sans-serif" fontWeight={800}>{item.value}</text>
        <text x={cx} y={cy + 2} fill={T.muted} fontSize={9} textAnchor="middle"
          fontFamily="Plus Jakarta Sans,sans-serif">{item.unit}</text>
      </svg>
      <Typography sx={{
        fontSize: 11, fontWeight: 700, color: item.color, mt: -1,
        fontFamily: "Plus Jakarta Sans,sans-serif"
      }}>{item.label}</Typography>
      <Typography sx={{ fontSize: 9.5, color: T.muted, fontFamily: "Plus Jakarta Sans,sans-serif" }}>
        {item.sub}
      </Typography>
    </Box>
  );
}

/* ══════════════════════════════════════════════════════
   MAIN DASHBOARD
══════════════════════════════════════════════════════ */
export default function ScrumMasterDashboard() {
  /* In production: replace MOCK with API response in useEffect → setData(apiResp) */
   const [data, setData] = useState(MOCK);

  const [isDark, setIsDark] = useState(false);
  const [clock, setClock] = useState("–");
  const [activeRow, setActiveRow] = useState(0);
  const [search, setSearch] = useState("");
  const T = isDark ? DARK : LIGHT;

  const CompId = sessionStorage.getItem("companyId");
  const empID = sessionStorage.getItem("EmpId");
  const dispatch = useDispatch();
  const params = useParams();

  const dashboardDataV2 = useSelector((state) => state.loginApi.dashboardDataV2);
   // const data = dashboardDataV2 || {};
    // const taskSummary = data?.taskSummaryCardData || [];
    // const taskTimeline = data?.taskTimeline || {};
    // const projectStats = data?.projectStats || [];
    // const taskAging = data?.taskAging || {};
    // const attendance = data?.attendance || {};
    // const leaveDetailsData = data?.leaveDetailsData || {};
    // const tasks = data?.tasks || {};
    // const tillDateTasks = data?.tillDateTasks || {};

  // const dashboardDataV2 = useSelector((state) => state.loginApi.dashBoard_V2);
  // const data = dashboardDataV2 || {};

  // useEffect(() => {
  //   dispatch(dashBoard_V2({ data: { EmployeeID: empID, Type: params.Type,CompanyID: CompId } }));
  // }, [dispatch, empID, params]);

  // 

  // const [data, setData] = useState();

//   useEffect(() => {
//   if (!empID || !params?.Type || !CompId) return;

//   console.log("API PARAMS 👉", {
//     EmployeeID: empID,
//     Type: params.Type,
//     CompanyID: CompId,
//   });

//   dispatch(
//     dashBoard_V2({
//       data: {
//         EmployeeID: empID,
//         Type: params.Type,
//         CompanyID: CompId,
//       },
//     })
//   );
// }, [dispatch, empID, params?.Type, CompId]);


  //  const dashboardData = data;
  // const dashboardData = useSelector((state) => state.loginApi.dashboardDataV2);
  // console.log("Json data", dashboardData);


  // useEffect(() => {

  //   if (!dashboardData) return;

  //   console.log("RAW DASHBOARD 👉", dashboardData);

  //   const apiData = dashboardData; //  important

  //   console.log("FINAL DATA 👉", apiData);

  //   setData({
  //     taskSummaryCardData: dashboardData.taskSummaryCardData || [],
  //     taskTimeline: dashboardData.taskTimeline || {},
  //     tasks: dashboardData.tasks || {},
  //     tillDateTasks: dashboardData.tillDateTasks || {},
  //     projectStats: dashboardData.projectStats || {},
  //     taskAging: dashboardData.taskAging || {},
  //     attendance: dashboardData.attendance || {},
  //     leaveDetailsData: dashboardData.leaveDetailsData || {},
  //     Colours: dashboardData.Colours || {},
  //   });
  // }, [dashboardData]);


  /* live clock */
  useEffect(() => {
    const tick = () => {
      const n = new Date();
      setClock(n.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
        + " " + n.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }));
    };
    tick(); const id = setInterval(tick, 1000); return () => clearInterval(id);
  }, []);

  /* filtered sprint table */
  const filteredSprints = useMemo(() =>
    (data.sprints || []).filter(s =>
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.id.toLowerCase().includes(search.toLowerCase())
    ), [data.sprints, search]);

  const activeSprint = data.sprints?.[activeRow] || data.sprints?.[0];

  /* ── Chart.js colour helpers ── */
  const tick = T.muted;
  const grid = T.grid;
  const baseOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: isDark ? "rgba(15,19,32,.95)" : "#fff",
        titleColor: isDark ? "#F0F4FF" : "#0f172a",
        bodyColor: isDark ? "#8892B0" : "#475569",
        borderColor: "rgba(100,100,100,.3)", borderWidth: 1,
        titleFont: { family: "Plus Jakarta Sans", size: 11, weight: "700" },
        bodyFont: { family: "Plus Jakarta Sans", size: 10 },
        padding: 10, cornerRadius: 8
      }
    },
    scales: {
      x: { grid: { color: grid, drawBorder: false }, ticks: { color: tick, font: chartFont, maxRotation: 0 } },
      y: { grid: { color: grid, drawBorder: false }, ticks: { color: tick, font: chartFont } },
    },
  };

  /* ─── Burndown ─── */
  const bdData = useMemo(() => ({
    labels: activeSprint?.burndown?.labels || [],
    datasets: [
      {
        label: "Ideal", data: activeSprint?.burndown?.ideal || [],
        borderColor: "#10b981", borderWidth: 1.8, borderDash: [6, 4],
        pointRadius: 3, pointBackgroundColor: "#10b981",
        fill: false, tension: .35,
      },
      {
        label: "Actual", data: activeSprint?.burndown?.actual || [],
        borderColor: "#3b82f6", borderWidth: 2.2,
        pointRadius: 5, pointBackgroundColor: "#3b82f6",
        pointBorderColor: T.stroke, pointBorderWidth: 2,
        fill: true, backgroundColor: "rgba(59,130,246,.08)", tension: .35,
      },
    ],
  }), [activeSprint, T.stroke]);

  const bdOpts = useMemo(() => ({
    ...baseOpts,
    plugins: {
      ...baseOpts.plugins,
      tooltip: {
        ...baseOpts.plugins.tooltip, mode: "index", intersect: false,
        callbacks: {
          afterBody: (items) => {
            const id = items.find(i => i.datasetIndex === 1);
            const ig = items.find(i => i.datasetIndex === 0);
            if (!id || !ig) return [];
            const d = ((id.parsed.y - ig.parsed.y) / Math.abs(ig.parsed.y || 1) * 100).toFixed(1);
            return [`${parseFloat(d) >= 0 ? "▲" : "▼"} vs Ideal: ${parseFloat(d) >= 0 ? "+" : ""}${d}%`];
          }
        }
      }
    },
  }), [baseOpts]);

  /* ─── Sprint Task Summary (grouped bar) ─── */
  const summData = useMemo(() => ({
    labels: data.sprintTaskSummary?.labels || [],
    datasets: [
      { label: "Backlog", data: data.sprintTaskSummary?.backlog || [], backgroundColor: "#3b82f6", borderRadius: 3, borderSkipped: false },
      { label: "Todo", data: data.sprintTaskSummary?.todo || [], backgroundColor: "#10b981", borderRadius: 3, borderSkipped: false },
      { label: "Completed", data: data.sprintTaskSummary?.completed || [], backgroundColor: "#f59e0b", borderRadius: 3, borderSkipped: false },
      { label: "Approved", data: data.sprintTaskSummary?.approved || [], backgroundColor: "#f97316", borderRadius: 3, borderSkipped: false },
    ],
  }), [data.sprintTaskSummary]);

  /* ─── Task Status donut ─── */
  const donutTotal = useMemo(() =>
    (data.taskStatus || []).reduce((a, d) => a + d.value, 0), [data.taskStatus]);
  const donutData = useMemo(() => ({
    labels: (data.taskStatus || []).map(d => d.label),
    datasets: [{
      data: (data.taskStatus || []).map(d => d.value),
      backgroundColor: (data.taskStatus || []).map(d => d.color),
      borderColor: T.stroke, borderWidth: 2.5, hoverOffset: 6,
    }],
  }), [data.taskStatus, T.stroke]);
  const donutOpts = useMemo(() => ({
    responsive: true, maintainAspectRatio: false, cutout: "72%",
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: isDark ? "rgba(15,19,32,.95)" : "#fff",
        titleColor: isDark ? "#F0F4FF" : "#0f172a", bodyColor: isDark ? "#8892B0" : "#475569",
        borderColor: "rgba(100,100,100,.3)", borderWidth: 1, padding: 10, cornerRadius: 8,
        titleFont: { family: "Plus Jakarta Sans", size: 11, weight: "700" },
        bodyFont: { family: "Plus Jakarta Sans", size: 10 },
        callbacks: { label: ctx => ` ${ctx.label}: ${ctx.parsed} (${((ctx.parsed / donutTotal) * 100).toFixed(1)}%)` }
      },
    },
  }), [isDark, donutTotal]);

  /* ─── Team Velocity ─── */
  const velData = useMemo(() => ({
    labels: data.teamVelocity?.labels || [],
    datasets: [
      { label: "Planned", data: data.teamVelocity?.planned || [], backgroundColor: "rgba(59,130,246,.4)", borderRadius: 3, borderSkipped: false },
      { label: "Actual", data: data.teamVelocity?.actual || [], backgroundColor: "#10b981", borderRadius: 3, borderSkipped: false },
    ],
  }), [data.teamVelocity]);

  /* ─── Cumulative Flow (stacked area) ─── */
  const cfdData = useMemo(() => ({
    labels: data.cumulativeFlow?.labels || [],
    datasets: [
      { label: "Backlog", data: data.cumulativeFlow?.backlog || [], borderColor: "#f43f5e", backgroundColor: "rgba(244,63,94,.15)", fill: true, tension: .4, borderWidth: 2, pointRadius: 3, pointBackgroundColor: "#f43f5e" },
      { label: "In Progress", data: data.cumulativeFlow?.inprog || [], borderColor: "#f59e0b", backgroundColor: "rgba(245,158,11,.18)", fill: true, tension: .4, borderWidth: 2, pointRadius: 3, pointBackgroundColor: "#f59e0b" },
      { label: "Done", data: data.cumulativeFlow?.done || [], borderColor: "#10b981", backgroundColor: "rgba(16,185,129,.2)", fill: true, tension: .4, borderWidth: 2, pointRadius: 3, pointBackgroundColor: "#10b981" },
    ],
  }), [data.cumulativeFlow]);

  /* ─── Bug Trend ─── */
  const bugData = useMemo(() => ({
    labels: data.bugTrend?.labels || [],
    datasets: [
      { label: "Opened", data: data.bugTrend?.opened || [], borderColor: "#f43f5e", backgroundColor: "rgba(244,63,94,.1)", fill: true, tension: .4, borderWidth: 2, pointRadius: 4, pointBackgroundColor: "#f43f5e", pointBorderColor: T.stroke, pointBorderWidth: 2 },
      { label: "Closed", data: data.bugTrend?.closed || [], borderColor: "#10b981", backgroundColor: "rgba(16,185,129,.1)", fill: true, tension: .4, borderWidth: 2, pointRadius: 4, pointBackgroundColor: "#10b981", pointBorderColor: T.stroke, pointBorderWidth: 2 },
    ],
  }), [data.bugTrend, T.stroke]);

  /* ─── Member Workload (stacked horizontal bar) ─── */
  const wlData = useMemo(() => ({
    labels: (data.memberWorkload || []).map(m => m.name),
    datasets: [
      { label: "Backlog", data: (data.memberWorkload || []).map(m => m.backlog), backgroundColor: "#3b82f6", borderSkipped: false, borderRadius: 0 },
      { label: "In Progress", data: (data.memberWorkload || []).map(m => m.inprog), backgroundColor: "#f59e0b", borderSkipped: false, borderRadius: 0 },
      { label: "Done", data: (data.memberWorkload || []).map(m => m.done), backgroundColor: "#10b981", borderSkipped: false, borderRadius: 0 },
      { label: "Blocked", data: (data.memberWorkload || []).map(m => m.blocked), backgroundColor: "#f43f5e", borderSkipped: false, borderRadius: 3 },
    ],
  }), [data.memberWorkload]);
  const wlOpts = useMemo(() => ({
    ...baseOpts, indexAxis: "y",
    scales: {
      x: { stacked: true, grid: { color: grid, drawBorder: false }, ticks: { color: tick, font: chartFont } },
      y: { stacked: true, grid: { display: false }, ticks: { color: tick, font: chartFont } },
    },
    plugins: {
      ...baseOpts.plugins,
      tooltip: { ...baseOpts.plugins.tooltip, mode: "index", intersect: false },
    },
  }), [baseOpts, grid, tick]);

  /* ─── Sprint Completion horizontal bar ─── */
  const progData = useMemo(() => ({
    labels: (data.sprintCompletion || []).map(i => i.label),
    datasets: [{
      data: (data.sprintCompletion || []).map(i => i.pct),
      backgroundColor: (data.sprintCompletion || []).map(i =>
        i.pct >= 100 ? "#10b981" : i.pct >= 75 ? "#3b82f6" : "#f59e0b"
      ),
      borderSkipped: false, borderRadius: 5,
    }],
  }), [data.sprintCompletion]);
  const progOpts = useMemo(() => ({
    ...baseOpts, indexAxis: "y",
    scales: {
      x: { min: 0, max: 100, grid: { color: grid, drawBorder: false }, ticks: { color: tick, font: chartFont, callback: v => v + "%" } },
      y: { grid: { display: false }, ticks: { color: tick, font: { ...chartFont, size: 8 } } },
    },
    plugins: {
      ...baseOpts.plugins,
      tooltip: {
        ...baseOpts.plugins.tooltip,
        callbacks: {
          label: ctx => {
            const item = data.sprintCompletion[ctx.dataIndex];
            return ` ${ctx.parsed.x}% (${item?.done}/${item?.total})`;
          }
        }
      },
    },
  }), [baseOpts, grid, tick, data.sprintCompletion]);

  const grdColor = isDark ? "rgba(59,130,246,.025)" : "rgba(59,130,246,.015)";

  /* ══════════════════ RENDER ══════════════════ */
  return (
    <Box sx={{
      fontFamily: "'Plus Jakarta Sans',sans-serif",
      bgcolor: T.bg, color: T.txt, minHeight: "100vh",
      fontSize: 13, transition: "background .3s,color .3s", position: "relative",
      overflow: "hidden",
    }}>
      <style>{`@keyframes smPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.4;transform:scale(1.6)}}`}</style>

      {/* grid overlay */}
      <Box sx={{
        position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0,
        backgroundImage: `linear-gradient(${grdColor} 1px,transparent 1px),linear-gradient(90deg,${grdColor} 1px,transparent 1px)`,
        backgroundSize: "40px 40px"
      }} />

      <Box sx={{
        position: "relative", zIndex: 1, width: "100%",
        maxWidth: "100%", mx: "auto", px: "22px", pb: "52px"
      }}>

        {/* ════ HEADER ════ */}
        <Box component="header" sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          py: "18px", borderBottom: `1px solid ${T.bdr}`, mb: "22px",
        }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {/* <Box sx={{
              width:38, height:38, background:"linear-gradient(135deg,#3b82f6,#8b5cf6)",
              borderRadius:"10px", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18,
            }}>⚡</Box> */}
            <Box>
              <Typography sx={{
                fontSize: 16, fontWeight: 800, letterSpacing: ".07em",
                textTransform: "uppercase", color: T.txt, fontFamily: "Plus Jakarta Sans,sans-serif"
              }}>
                Scrum Master Dashboard
              </Typography>
              {/* <Typography sx={{ fontSize:10, color:T.muted, mt:"2px", fontFamily:"Plus Jakarta Sans,sans-serif" }}>
                Sprint Command Center · Real-time Overview
              </Typography> */}
            </Box>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{
              bgcolor: T.s1, border: `1px solid ${T.bdr}`, borderRadius: "8px",
              px: "12px", py: "6px", fontSize: 11, color: T.muted,
              fontFamily: "'JetBrains Mono',monospace"
            }}>{clock}</Box>
            {/* theme toggle */}
            <Box onClick={() => setIsDark(d => !d)} sx={{
              width: 68, height: 34, borderRadius: "17px", border: `1px solid ${T.bdr}`,
              bgcolor: T.s2, display: "flex", alignItems: "center", p: "3px",
              cursor: "pointer", position: "relative",
            }}>
              <Box component="span" sx={{ position: "absolute", left: "8px", fontSize: 12, opacity: isDark ? 1 : .4 }}>🌙</Box>
              <Box sx={{
                width: 26, height: 26, borderRadius: "13px",
                background: "linear-gradient(135deg,#3b82f6,#8b5cf6)",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13,
                transform: isDark ? "translateX(0)" : "translateX(34px)", transition: "transform .3s",
              }}>{isDark ? "🌙" : "☀️"}</Box>
              <Box component="span" sx={{ position: "absolute", right: "8px", fontSize: 12, opacity: isDark ? .4 : 1 }}>☀️</Box>
            </Box>
            {[GroupsIcon, CalendarMonthIcon, SettingsIcon].map((Icon, i) => (
              <IconButton key={i} size="small" sx={{
                width: 34, height: 34, borderRadius: "8px", border: `1px solid ${T.bdr}`,
                bgcolor: T.s1, color: T.muted,
                "&:hover": { borderColor: "#3b82f6", color: "#3b82f6" },
              }}>
                <Icon fontSize="small" />
              </IconButton>
            ))}
          </Stack>
        </Box>

        {/* ════ STAT CARDS ════ */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "16px", mb: "20px" }}>
          {(data.taskSummaryCardData || []).map((c, i) => (
            <Paper key={i} elevation={0} sx={{
              background: c.gradient, borderRadius: "14px", p: "20px",
              position: "relative", overflow: "hidden", cursor: "pointer",
              transition: "transform .2s,box-shadow .2s",
              boxShadow: "0 6px 24px rgba(0,0,0,.25)",
              "&:hover": { transform: "translateY(-3px)", boxShadow: "0 14px 36px rgba(0,0,0,.35)" },
            }}>
              <Box sx={{
                position: "absolute", top: 16, right: 16, width: 38, height: 38,
                bgcolor: "rgba(255,255,255,.2)", borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, zIndex: 1
              }}>
                {c.ico}
              </Box>
              <Box sx={{
                position: "absolute", top: -24, right: -24, width: 100, height: 100,
                borderRadius: "50%", bgcolor: "rgba(255,255,255,.09)"
              }} />
              <Box sx={{
                position: "absolute", bottom: -30, right: 22, width: 60, height: 60,
                borderRadius: "50%", bgcolor: "rgba(255,255,255,.06)"
              }} />
              <Typography sx={{
                fontSize: 10, fontWeight: 700, letterSpacing: ".13em",
                textTransform: "uppercase", opacity: .9, mb: "5px", color: "#fff",
                fontFamily: "Plus Jakarta Sans,sans-serif"
              }}>{c.title}</Typography>
              <Typography sx={{
                fontSize: 44, fontWeight: 800, lineHeight: 1, mb: "12px",
                letterSpacing: -2, color: "#fff", fontFamily: "Plus Jakarta Sans,sans-serif"
              }}>{c.count}</Typography>
              <Stack direction="row" flexWrap="wrap" gap="5px">
                {c.breakdown.map((b, j) => (
                  <Box key={j} sx={{
                    bgcolor: "rgba(255,255,255,.22)", borderRadius: "20px",
                    px: "9px", py: "3px", fontSize: 10, fontWeight: 700, color: "#fff",
                    fontFamily: "Plus Jakarta Sans,sans-serif"
                  }}>
                    {b.label} ({b.value})
                  </Box>
                ))}
              </Stack>
            </Paper>
          ))}
        </Box>

        {/* ════ SPRINT OVERVIEW ════ */}
        <SectionLabel T={T}>Sprint Overview</SectionLabel>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", mb: "18px" }}>

          {/* Sprint List */}
          <Panel T={T} sx={{ display: "flex", flexDirection: "column", height: 350 }}>
            <PanelHeader dotColor="#06b6d4" title="List of Sprint" T={T}
              right={
                <TextField size="small" placeholder="Search…" value={search}
                  onChange={e => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 14, color: T.muted }} />
                    </InputAdornment>,
                    sx: { height: 32, fontSize: 11, bgcolor: T.s2, "& fieldset": { borderColor: T.bdr } },
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "7px" }, "& input": { color: T.txt, fontSize: 11 } }}
                />
              }
            />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: T.theadBg }}>
                    {["#", "ID", "Sprint Name", "Backlog", "Todo", "Done", "Approved", "Total"].map((h, i) => (
                      <TableCell key={i} align={i > 2 ? "center" : "left"} sx={{
                        fontSize: 9.5, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase",
                        color: T.muted, borderBottom: `1px solid ${T.bdr}`,
                        fontFamily: "Plus Jakarta Sans,sans-serif", py: "9px",
                      }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSprints.map((s, i) => {
                    const idx = (data.sprints || []).indexOf(s);
                    const isActive = idx === activeRow;
                    return (
                      <TableRow key={i} onClick={() => setActiveRow(idx)} sx={{
                        cursor: "pointer", borderBottom: `1px solid ${T.bdr}`,
                        bgcolor: isActive ? T.rowActive : "transparent",
                        borderLeft: isActive ? "3px solid #3b82f6" : "3px solid transparent",
                        "&:hover": { bgcolor: isDark ? "rgba(59,130,246,.07)" : "rgba(59,130,246,.05)" },
                      }}>
                        <TableCell sx={{ color: T.muted, fontSize: 10, py: "10px" }}>{i + 1}</TableCell>
                        <TableCell sx={{ py: "10px" }}>
                          <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: "#06b6d4", fontWeight: 500 }}>
                            {s.id}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: "10px" }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 11.5, color: T.txt, fontFamily: "Plus Jakarta Sans,sans-serif" }}>{s.name}</Typography>
                          <Typography sx={{ fontSize: 9, color: T.muted, mt: "2px", fontFamily: "Plus Jakarta Sans,sans-serif" }}>{s.date}</Typography>
                        </TableCell>
                        {[s.backlog, s.todo, s.completed, s.approved].map((v, j) => (
                          <TableCell key={j} align="center" sx={{
                            fontFamily: "'JetBrains Mono',monospace", fontSize: 11,
                            color: v > 0 ? "#10b981" : T.muted, fontWeight: v > 0 ? 700 : 400, py: "10px",
                          }}>{v}</TableCell>
                        ))}
                        <TableCell align="center" sx={{ py: "10px" }}>
                          <Box sx={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            minWidth: 22, height: 16, bgcolor: "#3b82f6", borderRadius: "8px",
                            fontSize: 10, fontWeight: 800, color: "#fff", px: "5px"
                          }}>{s.total}</Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{
              px: 2, py: "10px", display: "flex", alignItems: "center", justifyContent: "space-between",
              background: "linear-gradient(90deg,#06b6d4,#3b82f6)",
            }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#fff", fontFamily: "Plus Jakarta Sans,sans-serif" }}>
                1–{filteredSprints.length} of {filteredSprints.length}
              </Typography>
              <Stack direction="row" spacing={0.8}>
                {[NavigateBeforeIcon, NavigateNextIcon].map((Icon, i) => (
                  <Box key={i} sx={{
                    width: 24, height: 24, borderRadius: "5px",
                    bgcolor: "rgba(255,255,255,.22)", display: "flex",
                    alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff",
                    "&:hover": { bgcolor: "rgba(255,255,255,.38)" },
                  }}><Icon sx={{ fontSize: 16 }} /></Box>
                ))}
              </Stack>
            </Box>
          </Panel>

          {/* Burndown */}
          <Panel T={T} sx={{ display: "flex", flexDirection: "column" }}>
            <PanelHeader dotColor="#8b5cf6" title="Sprint Burndown" T={T}
              right={
                <Typography sx={{ fontSize: 9.5, color: T.muted, fontFamily: "'JetBrains Mono',monospace" }}>
                  {activeSprint?.date}
                </Typography>
              }
            />
            <Typography sx={{ fontSize: 10, color: "#06b6d4", fontFamily: "'JetBrains Mono',monospace", px: 2, pb: 1 }}>
              {activeSprint?.id} · {activeSprint?.name}
            </Typography>
            {/* legend */}
            <Stack direction="row" spacing={2} sx={{ px: 2, pb: 1 }}>
              {[["#10b981", "Ideal", "dashed"], ["#3b82f6", "Actual", "solid"]].map(([c, l, t]) => (
                <Stack key={l} direction="row" alignItems="center" spacing={0.6}>
                  <Box sx={{ width: 16, height: 0, borderTop: `2px ${t} ${c}` }} />
                  <Typography sx={{ fontSize: 9, color: c, fontWeight: 600, fontFamily: "Plus Jakarta Sans,sans-serif" }}>{l}</Typography>
                </Stack>
              ))}
            </Stack>
            <Box sx={{ flex: 1, px: 2, pb: 2, minHeight: 220 }}>
              <Line data={bdData} options={bdOpts} />
            </Box>
          </Panel>
        </Box>

        {/* ════ SPRINT ANALYTICS ════ */}
        <SectionLabel T={T}>Sprint Analytics</SectionLabel>
        <Box sx={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr", gap: "16px", mb: "18px" }}>

          {/* Sprint Task Summary */}
          <Panel T={T}>
            <PanelHeader dotColor="#f59e0b" title="Sprint Task Summary" T={T}
              right={<Typography sx={{ fontSize: 10, color: T.muted, fontFamily: "Plus Jakarta Sans,sans-serif" }}>Tasks per sprint</Typography>}
            />
            <Box sx={{ p: "14px 16px 10px", height: 220 }}>
              <Bar data={summData} options={{
                ...baseOpts, scales: {
                  ...baseOpts.scales,
                  x: { ...baseOpts.scales.x, stacked: false },
                  y: { ...baseOpts.scales.y, beginAtZero: true },
                }
              }} />
            </Box>
            <Stack direction="row" flexWrap="wrap" gap="12px" sx={{ px: 2, pb: 2 }}>
              {[["#3b82f6", "Backlog"], ["#10b981", "Todo"], ["#f59e0b", "Completed"], ["#f97316", "Approved"]].map(([c, l]) => (
                <LegendDot key={l} color={c} label={l} T={T} />
              ))}
            </Stack>
          </Panel>

          {/* Donut */}
          <Panel T={T}>
            <PanelHeader dotColor="#10b981" title="Task Status" T={T}
              right={<Typography sx={{ fontSize: 10, color: T.muted, fontFamily: "Plus Jakarta Sans,sans-serif" }}>Overall</Typography>}
            />
            <Box sx={{ p: "14px 16px 4px", display: "flex", justifyContent: "center", position: "relative", height: 145 }}>
              <Doughnut data={donutData} options={donutOpts} />
              <Box sx={{
                position: "absolute", top: "50%", left: "50%",
                transform: "translate(-50%,-50%)", textAlign: "center", pointerEvents: "none"
              }}>
                <Typography sx={{
                  fontSize: 22, fontWeight: 800, color: T.txt, lineHeight: 1,
                  fontFamily: "Plus Jakarta Sans,sans-serif"
                }}>{donutTotal}</Typography>
                <Typography sx={{ fontSize: 9, color: T.muted, fontFamily: "Plus Jakarta Sans,sans-serif" }}>Total Tasks</Typography>
              </Box>
            </Box>
            <Stack spacing={0.7} sx={{ px: 2, pb: 2 }}>
              {(data.taskStatus || []).map((d, i) => (
                <Stack key={i} direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={0.6}>
                    <Box sx={{ width: 9, height: 9, borderRadius: "2px", bgcolor: d.color, flexShrink: 0 }} />
                    <Typography sx={{ color: T.muted, fontSize: 10.5, fontWeight: 600, fontFamily: "Plus Jakarta Sans,sans-serif" }}>{d.label}</Typography>
                  </Stack>
                  <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: T.txt, fontWeight: 700 }}>{d.value}</Typography>
                </Stack>
              ))}
            </Stack>
          </Panel>

          {/* Velocity */}
          <Panel T={T}>
            <PanelHeader dotColor="#f43f5e" title="Team Velocity" T={T}
              right={<Typography sx={{ fontSize: 10, color: T.muted, fontFamily: "Plus Jakarta Sans,sans-serif" }}>Hours/sprint</Typography>}
            />
            <Box sx={{ p: "14px 16px 10px", height: 220 }}>
              <Bar data={velData} options={{
                ...baseOpts, scales: {
                  x: { ...baseOpts.scales.x, grouped: true },
                  y: { ...baseOpts.scales.y, beginAtZero: true },
                }
              }} />
            </Box>
            <Stack direction="row" flexWrap="wrap" gap="12px" sx={{ px: 2, pb: 2 }}>
              <LegendDot color="rgba(59,130,246,.5)" label="Planned" T={T} />
              <LegendDot color="#10b981" label="Actual" T={T} />
            </Stack>
          </Panel>
        </Box>

        {/* ════ FLOW & PROGRESS ════ */}
        <SectionLabel T={T}>Flow & Progress</SectionLabel>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", mb: "18px" }}>

          {/* CFD */}
          <Panel T={T}>
            <PanelHeader dotColor="#06b6d4" title="Cumulative Flow Diagram" T={T}
              right={<Typography sx={{ fontSize: 10, color: T.muted, fontFamily: "Plus Jakarta Sans,sans-serif" }}>Task state over time</Typography>}
            />
            <Box sx={{ p: "14px 16px 10px", height: 230 }}>
              <Line data={cfdData} options={{
                ...baseOpts,
                plugins: { ...baseOpts.plugins, tooltip: { ...baseOpts.plugins.tooltip, mode: "index", intersect: false } },
              }} />
            </Box>
            <Stack direction="row" flexWrap="wrap" gap="12px" sx={{ px: 2, pb: 2 }}>
              {[["#f43f5e", "Backlog"], ["#f59e0b", "In Progress"], ["#10b981", "Done"]].map(([c, l]) => (
                <LegendDot key={l} color={c} label={l} T={T} />
              ))}
            </Stack>
          </Panel>

          {/* Sprint Completion */}
          <Panel T={T}>
            <PanelHeader dotColor="#8b5cf6" title="Sprint Completion" T={T}
              right={<Typography sx={{ fontSize: 10, color: T.muted, fontFamily: "Plus Jakarta Sans,sans-serif" }}>Progress %</Typography>}
            />
            <Box sx={{ p: "14px 16px 10px", height: 230 }}>
              <Bar data={progData} options={progOpts} />
            </Box>
            <Stack direction="row" flexWrap="wrap" gap="12px" sx={{ px: 2, pb: 2 }}>
              <LegendDot color="#10b981" label="Completed" T={T} />
              <Stack direction="row" alignItems="center" spacing={0.6}>
                <Box sx={{
                  width: 9, height: 9, borderRadius: "2px", bgcolor: T.barBg,
                  border: `1px solid ${T.bdr2}`, flexShrink: 0
                }} />
                <Typography sx={{ fontSize: 10.5, color: T.muted, fontWeight: 600, fontFamily: "Plus Jakarta Sans,sans-serif" }}>Remaining</Typography>
              </Stack>
            </Stack>
          </Panel>
        </Box>

        {/* ════ QUALITY & EFFICIENCY ════ */}
        <SectionLabel T={T}>Quality & Efficiency</SectionLabel>
        <Box sx={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "16px", mb: "18px" }}>

          {/* Bug Trend */}
          <Panel T={T}>
            <PanelHeader dotColor="#f43f5e" title="Bug / Defect Trend" T={T}
              right={<Typography sx={{ fontSize: 10, color: T.muted, fontFamily: "Plus Jakarta Sans,sans-serif" }}>Opened vs Closed per sprint</Typography>}
            />
            <Box sx={{ p: "14px 16px 10px", height: 230 }}>
              <Line data={bugData} options={{
                ...baseOpts,
                plugins: { ...baseOpts.plugins, tooltip: { ...baseOpts.plugins.tooltip, mode: "index", intersect: false } },
              }} />
            </Box>
            <Stack direction="row" flexWrap="wrap" gap="12px" sx={{ px: 2, pb: 2 }}>
              <LegendDot color="#f43f5e" label="Opened" T={T} />
              <LegendDot color="#10b981" label="Closed" T={T} />
            </Stack>
          </Panel>

          {/* Lead & Cycle Time */}
          <Panel T={T}>
            <PanelHeader dotColor="#f59e0b" title="Lead & Cycle Time" T={T}
              right={<Typography sx={{ fontSize: 10, color: T.muted, fontFamily: "Plus Jakarta Sans,sans-serif" }}>Avg days per task</Typography>}
            />
            <Stack direction="row" justifyContent="center" spacing={2} sx={{ p: "14px 16px 14px" }}>
              {(data.leadCycleTime || []).map((item, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <Box sx={{ width: "1px", bgcolor: T.bdr, my: 2 }} />}
                  <Gauge item={item} T={T} />
                </React.Fragment>
              ))}
            </Stack>
          </Panel>
        </Box>

        {/* ════ TEAM WORKLOAD ════ */}
        <SectionLabel T={T}>Team Workload</SectionLabel>
        <Panel T={T} sx={{ mb: "18px" }}>
          <PanelHeader dotColor="#8b5cf6" title="Member Workload Distribution" T={T}
            right={<Typography sx={{ fontSize: 10, color: T.muted, fontFamily: "Plus Jakarta Sans,sans-serif" }}>Tasks assigned per member</Typography>}
          />
          <Box sx={{ p: "14px 16px 10px", height: 260 }}>
            <Bar data={wlData} options={wlOpts} />
          </Box>
          <Stack direction="row" flexWrap="wrap" gap="12px" sx={{ px: 2, pb: 2 }}>
            {[["#3b82f6", "Backlog"], ["#f59e0b", "In Progress"], ["#10b981", "Done"], ["#f43f5e", "Blocked"]].map(([c, l]) => (
              <LegendDot key={l} color={c} label={l} T={T} />
            ))}
          </Stack>
        </Panel>

      </Box>
    </Box>
  );
}


// import React, { useState } from "react";
// import {
//     Box, Grid, Typography, Paper, Chip, Table, TableBody,
//     TableCell, TableContainer, TableHead, TableRow, LinearProgress,
//     Avatar, IconButton, InputAdornment, TextField, Divider
// } from "@mui/material";
// import {
//     BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
//     Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
//     AreaChart, Area
// } from "recharts";
// import SearchIcon from "@mui/icons-material/Search";
// import FilterListIcon from "@mui/icons-material/FilterList";
// import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
// import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
// import SettingsIcon from "@mui/icons-material/Settings";
// import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
// import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
// import AssignmentIcon from "@mui/icons-material/Assignment";
// import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
// import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
// import StarBorderIcon from "@mui/icons-material/StarBorder";

// /* ─── COLOUR TOKENS ─── */
// const C = {
//     backlog: "#e74c3c",
//     todo: "#3498db",
//     completed: "#2ecc71",
//     total: "#f39c12",
//     bg: "#f4f6f8",
//     card: "#ffffff",
//     text1: "#2c3e50",
//     text2: "#7f8c8d",
//     border: "#e8ecf0",
//     green: "#27ae60",
//     orange: "#f39c12",
//     blue: "#2980b9",
//     red: "#e74c3c",
//     purple: "#9b59b6",
//     teal: "#1abc9c",
// };

// /* ─── STATIC DATA ─── */
// const sprintList = [
//     {
//         id: "SH060",
//         name: "QA Engineer",
//         backlog: 0,
//         todo: 0,
//         done: 1,
//         approved: 1,
//         total: 2,
//         color: "#3b82f6",
//     },
//     {
//         id: "SH062",
//         name: "UI/UX Design",
//         backlog: 0,
//         todo: 0,
//         done: 1,
//         approved: 0,
//         total: 1,
//         color: "#3b82f6",
//     },
//     {
//         id: "SH063",
//         name: "SYS PROJECT",
//         backlog: 0,
//         todo: 0,
//         done: 8,
//         approved: 0,
//         total: 8,
//         color: "#3b82f6",
//     },
// ];

// const burndownData = [
//     { day: "Jul 11", ideal: 3.2, actual: 3.2 },
//     { day: "Jul 12", ideal: 2.4, actual: 2.7 },
//     { day: "Jul 13", ideal: 1.6, actual: 1.8 },
//     { day: "Jul 14", ideal: 0.8, actual: 1.3 },
//     { day: "Jul 15", ideal: 0.0, actual: 0.7 },
// ];

// const sprintSummaryData = [
//     { sprint: "SH060", backlog: 0, todo: 0, completed: 1, approved: 1 },
//     { sprint: "SH062", backlog: 0, todo: 0, completed: 1, approved: 0 },
//     { sprint: "SH063", backlog: 0, todo: 0, completed: 8, approved: 0 },
// ];

// const taskStatusData = [
//     { name: "Backlog", value: 23, color: "#ef4444" },   // red
//     { name: "To Do", value: 1, color: "#3b82f6" },      // blue
//     { name: "Completed", value: 340, color: "#10b981" },// green
//     { name: "Blocked", value: 8, color: "#8b5cf6" },    // purple
//     { name: "Approved", value: 75, color: "#f59e0b" },  // orange
// ];

// const velocityData = [
//     { sprint: "S1", planned: 30, actual: 28 },
//     { sprint: "S2", planned: 35, actual: 32 },
//     { sprint: "S3", planned: 28, actual: 30 },
//     { sprint: "S4", planned: 40, actual: 35 },
//     { sprint: "S5", planned: 32, actual: 38 },
//     { sprint: "S6", planned: 38, actual: 40 },
// ];

// const cfdData = [
//     { date: "Jun 01", done: 0, inProgress: 5, backlog: 80 },
//     { date: "Jun 07", done: 10, inProgress: 15, backlog: 70 },
//     { date: "Jun 13", done: 25, inProgress: 20, backlog: 55 },
//     { date: "Jun 19", done: 45, inProgress: 18, backlog: 42 },
//     { date: "Jun 25", done: 65, inProgress: 15, backlog: 28 },
//     { date: "Jun 29", done: 80, inProgress: 10, backlog: 15 },
// ];

// const sprintCompletion = [
//     { label: "Story Goals", completed: 65, remaining: 35 },
//     { label: "Sprint Goals", completed: 100, remaining: 0 },
//     { label: "Story Closing", completed: 88, remaining: 12 },
// ];

// const bugTrendData = [
//     { date: "Jun 01", opened: 8, closed: 3 },
//     { date: "Jun 05", opened: 12, closed: 7 },
//     { date: "Jun 09", opened: 6, closed: 10 },
//     { date: "Jun 13", opened: 15, closed: 8 },
//     { date: "Jun 17", opened: 9, closed: 13 },
//     { date: "Jun 21", opened: 4, closed: 11 },
//     { date: "Jun 25", opened: 7, closed: 9 },
//     { date: "Jun 29", opened: 10, closed: 12 },
// ];

// const memberWorkload = [
//     { name: "Alice", backlog: 3, inProgress: 5, done: 8, blocked: 1 },
//     { name: "Bob", backlog: 5, inProgress: 8, done: 12, blocked: 2 },
//     { name: "Carol", backlog: 2, inProgress: 3, done: 15, blocked: 0 },
//     { name: "David", backlog: 7, inProgress: 6, done: 9, blocked: 3 },
//     { name: "Emma", backlog: 1, inProgress: 4, done: 20, blocked: 1 },
//     { name: "Frank", backlog: 4, inProgress: 7, done: 11, blocked: 0 },
// ];

// /* ─── HELPER COMPONENTS ─── */

// /** Coloured stat card at the very top */
// function StatCard({ title, count, color, icon, chips }) {
//     return (
//         <Paper elevation={0} sx={{
//             background: color, borderRadius: 4, p: 1.5, color: "#fff",
//             height: "100%", position: "relative", overflow: "hidden",
//         }}>
//             <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
//                 <Box>
//                     <Typography sx={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", opacity: 0.85, letterSpacing: 0.5 }}>
//                         {title}
//                     </Typography>
//                     <Typography sx={{ fontSize: 45, fontWeight: 800, lineHeight: 1.1, mt: 0.5 }}>
//                         {count}
//                     </Typography>
//                 </Box>
//                 <Box sx={{
//                     width: 36, height: 36, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.2)",
//                     display: "flex", alignItems: "center", justifyContent: "center",
//                 }}>
//                     {icon}
//                 </Box>
//             </Box>
//             <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap", mt: 1 }}>
//                 {chips.map((c, i) => (
//                     <Chip key={i} label={c} size="small" sx={{
//                         height: 18, fontSize: 11, fontWeight: 600,
//                         bgcolor: "rgba(255,255,255,0.25)", color: "#fff",
//                         "& .MuiChip-label": { px: 0.8 },
//                     }} />
//                 ))}
//             </Box>
//         </Paper>
//     );
// }

// /** Section wrapper with title + optional right content */
// function Section({ title, right, children, noPad }) {
//     return (
//         <Paper elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: 2, overflow: "hidden" }}>
//             <Box sx={{
//                 px: 2, py: 1, display: "flex", alignItems: "center",
//                 justifyContent: "space-between", borderBottom: `1px solid ${C.border}`,
//                 bgcolor: "#fafbfc",
//             }}>
//                 <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
//                     <Box sx={{ width: 3, height: 14, borderRadius: 2, bgcolor: C.blue }} />
//                     <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.text1, textTransform: "uppercase", letterSpacing: 0.4 }}>
//                         {title}
//                     </Typography>
//                 </Box>
//                 {right && <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>{right}</Box>}
//             </Box>
//             <Box sx={noPad ? {} : { p: 1.5 }}>{children}</Box>
//         </Paper>
//     );
// }

// /** Small badge */
// function Badge({ label, color }) {
//     return (
//         <Chip label={label} size="small" sx={{
//             height: 17, fontSize: 9, fontWeight: 700,
//             bgcolor: color + "22", color, "& .MuiChip-label": { px: 0.7 },
//         }} />
//     );
// }

// /** Gauge needle for Lead/Cycle Time */
// function Gauge({ value, max, color, label, sub }) {
//     const pct = Math.min(value / max, 1);
//     const angle = -140 + pct * 280;
//     const r = 52, cx = 70, cy = 68;
//     const track = (start, end, stroke, sw = 10) => {
//         const s = (start * Math.PI) / 180, e = (end * Math.PI) / 180;
//         const x1 = cx + r * Math.cos(s), y1 = cy + r * Math.sin(s);
//         const x2 = cx + r * Math.cos(e), y2 = cy + r * Math.sin(e);
//         const large = end - start > 180 ? 1 : 0;
//         return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
//     };
//     return (
//         <Box sx={{ textAlign: "center", flex: 1 }}>
//             <svg width={140} height={90} viewBox="0 0 140 90">
//                 <path d={track(220, 320, "#eee")} fill="none" stroke="#eaeaea" strokeWidth={10} strokeLinecap="round" />
//                 <path d={track(220, 220 + pct * 100, color)} fill="none" stroke={color} strokeWidth={10} strokeLinecap="round" />
//                 <line
//                     x1={cx} y1={cy}
//                     x2={cx + 38 * Math.cos((angle * Math.PI) / 180)}
//                     y2={cy + 38 * Math.sin((angle * Math.PI) / 180)}
//                     stroke={color} strokeWidth={2} strokeLinecap="round"
//                 />
//                 <circle cx={cx} cy={cy} r={4} fill={color} />
//                 <text x={cx} y={cy - 12} textAnchor="middle" fontSize={16} fontWeight={800} fill={color}>{value}</text>
//                 <text x={cx} y={cy - 2} textAnchor="middle" fontSize={8} fill={C.text2}>days</text>
//             </svg>
//             <Typography sx={{ fontSize: 10, fontWeight: 700, color, mt: -1 }}>{label}</Typography>
//             <Typography sx={{ fontSize: 9, color: C.text2 }}>{sub}</Typography>
//         </Box>
//     );
// }

// /* ─── MAIN DASHBOARD ─── */
// export default function ScrumMasterDashboard() {
//     const [search, setSearch] = useState("");

//     const filtered = sprintList.filter(s =>
//         s.name.toLowerCase().includes(search.toLowerCase()) ||
//         s.id.toLowerCase().includes(search.toLowerCase())
//     );

//     const taskTotal = taskStatusData.reduce((a, b) => a + b.value, 0);

//     return (
//         <Box sx={{ bgcolor: C.bg, minHeight: "100vh", fontFamily: "'Segoe UI', sans-serif" }}>
//             {/* ── TOPBAR ── */}
//             <Box sx={{
//                 bgcolor: "#fff", borderBottom: `1px solid ${C.border}`,
//                 px: 3, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between",
//                 position: "sticky", top: 0, zIndex: 100,
//             }}>
//                 <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                     <Box sx={{
//                         width: 28, height: 28, borderRadius: 1, bgcolor: C.blue,
//                         display: "flex", alignItems: "center", justifyContent: "center",
//                     }}>
//                         <AssignmentIcon sx={{ color: "#fff", fontSize: 16 }} />
//                     </Box>
//                     <Typography sx={{ fontSize: 13, fontWeight: 800, color: C.text1, letterSpacing: 0.3 }}>
//                         SCRUM MASTER DASHBOARD
//                     </Typography>
//                 </Box>
//                 <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
//                     <Typography sx={{ fontSize: 10, color: C.text2, mr: 1 }}>
//                         07 Apr 2024  14:37
//                     </Typography>
//                     {[NotificationsNoneIcon, PersonOutlineIcon, SettingsIcon].map((Icon, i) => (
//                         <IconButton key={i} size="small" sx={{ p: 0.5 }}>
//                             <Icon sx={{ fontSize: 18, color: C.text2 }} />
//                         </IconButton>
//                     ))}
//                 </Box>
//             </Box>

//             {/* ── BODY ── */}
//             <Box sx={{ p: 2 }}>

//                 {/* ── ROW 1: 4 STAT CARDS ── */}
//                 <Grid container spacing={1.5} sx={{ mb: 3.5 }}>
//                     {[
//                         {
//                             title: "Backlog Tasks",
//                             count: 23,
//                             color: C.backlog,
//                             icon: <AssignmentIcon sx={{ fontSize: 18 }} />,
//                             chips: ["Self (4)", "Manager (5)", "Sprint (14)"],
//                         },
//                         {
//                             title: "To Do Tasks",
//                             count: 1,
//                             color: C.todo,
//                             icon: <PlayCircleOutlineIcon sx={{ fontSize: 18 }} />,
//                             chips: ["Self (1)", "Manager (0)", "Sprint (0)"],
//                         },
//                         {
//                             title: "Completed Tasks",
//                             count: 340,
//                             color: C.completed,
//                             icon: <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />,
//                             chips: ["Self (326)", "Manager (10)", "Sprint (4)"],
//                         },
//                         {
//                             title: "Total Tasks",
//                             count: 98,
//                             color: C.total,
//                             icon: <StarBorderIcon sx={{ fontSize: 18 }} />,
//                             chips: ["SPA (15)", "Approved (75)", "Blocked (8)"],
//                         },
//                     ].map((s, i) => (
//                         <Grid item xs={12} sm={6} md={3} key={i}>
//                             <StatCard {...s} />
//                         </Grid>
//                     ))}
//                 </Grid>

//                 <Typography sx={{ fontSize: 10, fontWeight: 700, color: C.text2, textTransform: "uppercase", letterSpacing: 0.8, mb: 1 }}>
//                     Sprint Overview
//                 </Typography>
//                 {/* ── ROW 2: Sprint List + Burndown ── */}
//                 <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
//                     {/* Sprint List */}
//                     <Grid item xs={12} md={6}>
//                         <Section
//                             title="List of Sprint"
//                             right={
//                                 <TextField
//                                     size="small"
//                                     placeholder="Search..."
//                                     value={search}
//                                     onChange={e => setSearch(e.target.value)}
//                                     InputProps={{
//                                         startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 14 }} /></InputAdornment>,
//                                         sx: { fontSize: 11, height: 28 },
//                                     }}
//                                     sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1, fontSize: 11 }, width: 140 }}
//                                 />
//                             }
//                             noPad
//                         >
//                             <TableContainer>
//                                 <Table
//                                     size="small"
//                                     sx={{
//                                         "& th": {
//                                             fontSize: 11,
//                                             fontWeight: 700,
//                                             color: C.text2,
//                                             textTransform: "uppercase",
//                                             py: 0.8,
//                                             px: 1.2,
//                                         },
//                                         "& td": {
//                                             fontSize: 13,
//                                             py: 1,
//                                             px: 1.2,
//                                             fontWeight: 600,
//                                         },
//                                     }}
//                                 >
//                                     <TableHead>
//                                         <TableRow
//                                             sx={{
//                                                 bgcolor: "#f1f5f9",
//                                                 height: 34,
//                                             }}
//                                         >
//                                             {["#", "ID", "Sprint Name", "Backlog", "Todo", "Done", "Approved", "Total"].map((h) => (
//                                                 <TableCell key={h}>{h}</TableCell>
//                                             ))}
//                                         </TableRow>
//                                     </TableHead>

//                                     <TableBody>
//                                         {filtered.map((row, i) => (
//                                             <TableRow
//                                                 key={i}
//                                                 hover
//                                                 sx={{
//                                                     height: 42,
//                                                     transition: "0.2s",
//                                                     "&:hover": {
//                                                         bgcolor: "#f8fafc",
//                                                     },
//                                                 }}
//                                             >
//                                                 <TableCell sx={{ width: 30, textAlign: "center", color: C.text2 }}>
//                                                     {i + 1}
//                                                 </TableCell>

//                                                 <TableCell sx={{ color: C.blue, fontWeight: 700 }}>
//                                                     {row.id}
//                                                 </TableCell>

//                                                 <TableCell sx={{ fontWeight: 700, color: C.text1 }}>
//                                                     {row.name}
//                                                 </TableCell>

//                                                 <TableCell sx={{ color: C.backlog }}>{row.backlog}</TableCell>
//                                                 <TableCell sx={{ color: C.todo }}>{row.todo}</TableCell>
//                                                 <TableCell sx={{ color: C.green }}>{row.done}</TableCell>
//                                                 <TableCell sx={{ color: C.purple }}>{row.approved}</TableCell>

//                                                 <TableCell>
//                                                     <Badge label={row.total} color={row.color} />
//                                                 </TableCell>
//                                             </TableRow>
//                                         ))}
//                                     </TableBody>
//                                 </Table>
//                             </TableContainer>
//                             <Box sx={{
//                                 bgcolor: C.blue, px: 1.5, py: 0.6, display: "flex",
//                                 justifyContent: "space-between", alignItems: "center",
//                             }}>
//                                 <Typography sx={{ fontSize: 10, color: "#fff", fontWeight: 600 }}>Total</Typography>
//                                 <Box
//                                     sx={{
//                                         bgcolor: C.blue,
//                                         px: 2,
//                                         py: 1,
//                                         display: "flex",
//                                         justifyContent: "space-between",
//                                         alignItems: "center",
//                                         borderBottomLeftRadius: 8,
//                                         borderBottomRightRadius: 8,
//                                     }}
//                                 >
//                                     <Typography sx={{ fontSize: 11, color: "#fff", fontWeight: 700 }}>
//                                         1–3 of 3
//                                     </Typography>

//                                     <Box sx={{ display: "flex", gap: 1 }}>
//                                         <Box sx={navBtnStyle}>‹</Box>
//                                         <Box sx={navBtnStyle}>›</Box>
//                                     </Box>
//                                 </Box>
//                             </Box>
//                         </Section>
//                     </Grid>

//                     {/* Sprint Burndown */}
//                     <Grid item xs={12} md={6} >
//                         <Section
//                             title="Sprint Burndown"

//                             right={
//                                 <Box sx={{ display: "flex", gap: 1.5 }}>
//                                     {[["To all", C.blue], ["M / PEG / MAST", C.text2]].map(([l, c], i) => (
//                                         <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
//                                             <Box sx={{ width: 16, height: 2, bgcolor: c }} />
//                                             <Typography sx={{ fontSize: 9, color: c }}>{l}</Typography>
//                                         </Box>
//                                     ))}
//                                 </Box>
//                             }
//                         >

//                             <ResponsiveContainer width="100%" height={200}>
//                                 <LineChart data={burndownData} margin={{ top: 4, right: 8, bottom: 4, }}>
//                                     <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
//                                     <XAxis dataKey="day" tick={{ fontSize: 8, fill: C.text2 }} />
//                                     <YAxis tick={{ fontSize: 8, fill: C.text2 }} />
//                                     <Tooltip contentStyle={{ fontSize: 10 }} />
//                                     <Line type="monotone" dataKey="ideal" stroke={C.text2} strokeDasharray="4 4" dot={false} strokeWidth={1.5} name="Ideal" />
//                                     <Line type="monotone" dataKey="actual" stroke={C.blue} dot={{ r: 3, fill: C.blue }} strokeWidth={2} name="Actual" />
//                                 </LineChart>
//                             </ResponsiveContainer>
//                         </Section>
//                     </Grid>
//                 </Grid>

//                 {/* ── SPRINT ANALYTICS HEADER ── */}
//                 <Typography sx={{ fontSize: 10, fontWeight: 700, color: C.text2, textTransform: "uppercase", letterSpacing: 0.8, mb: 1 }}>
//                     Sprint Analytics
//                 </Typography>

//                 {/* ── ROW 3: Sprint Summary + Task Status + Team Velocity ── */}
//                 <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
//                     {/* Sprint Task Summary */}
//                     <Grid item xs={12} md={4}>
//                         <Section title="Sprint Task Summary" right={<Badge label="Tasks/Sprint" color={C.blue} />}>
//                             <ResponsiveContainer width="100%" height={150}>
//                                 <BarChart data={sprintSummaryData} margin={{ top: 4, right: 4, bottom: 4, left: -20 }}>
//                                     <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
//                                     <XAxis dataKey="sprint" tick={{ fontSize: 8 }} />
//                                     <YAxis tick={{ fontSize: 8 }} />
//                                     <Tooltip contentStyle={{ fontSize: 10 }} />
//                                     <Bar dataKey="backlog" fill={C.backlog} radius={[2, 2, 0, 0]} barSize={6} />
//                                     <Bar dataKey="todo" fill={C.todo} radius={[2, 2, 0, 0]} barSize={6} />
//                                     <Bar dataKey="completed" fill={C.completed} radius={[2, 2, 0, 0]} barSize={6} />
//                                     <Bar dataKey="approved" fill={C.purple} radius={[2, 2, 0, 0]} barSize={6} />
//                                 </BarChart>
//                             </ResponsiveContainer>
//                             <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 0.5 }}>
//                                 {[["Backlog", C.backlog], ["Todo", C.todo], ["Completed", C.completed], ["Approved", C.purple]].map(([l, c]) => (
//                                     <Box key={l} sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
//                                         <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: c }} />
//                                         <Typography sx={{ fontSize: 9, color: C.text2 }}>{l}</Typography>
//                                     </Box>
//                                 ))}
//                             </Box>
//                         </Section>
//                     </Grid>

//                     {/* Task Status */}
//                     <Grid item xs={12} md={4}>
//                         <Section
//                             title="Task Status"
//                             right={
//                                 <Typography sx={{ fontSize: 9, color: C.text2 }}>
//                                     Overall
//                                 </Typography>
//                             }
//                             sx={{height: 150}}
//                         >
//                             <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>

//                                 {/* Donut */}
//                                 <Box sx={{ position: "relative", width: 120, height: 170 }}>
//                                     <PieChart width={120} height={120}>
//                                         <Pie
//                                             data={taskStatusData}
//                                             cx={60}
//                                             cy={60}
//                                             innerRadius={38}
//                                             outerRadius={52}
//                                             dataKey="value"
//                                             startAngle={90}
//                                             endAngle={-270}
//                                             paddingAngle={1}
//                                         >
//                                             {taskStatusData.map((e, i) => (
//                                                 <Cell key={i} fill={e.color} />
//                                             ))}
//                                         </Pie>
//                                     </PieChart>

//                                     {/* Center */}
//                                     <Box
//                                         sx={{
//                                             position: "absolute",
//                                             top: "50%",
//                                             left: "50%",
//                                             transform: "translate(-50%, -50%)",
//                                             textAlign: "center",
//                                         }}
//                                     >
//                                         <Typography
//                                             sx={{
//                                                 fontSize: 16,
//                                                 fontWeight: 800,
//                                                 color: C.text1,
//                                                 lineHeight: 1,
//                                             }}
//                                         >
//                                             447
//                                         </Typography>
//                                         <Typography sx={{ fontSize: 8, color: C.text2 }}>
//                                             Total Tasks
//                                         </Typography>
//                                     </Box>
//                                 </Box>

//                                 {/* Legend */}
//                                 <Box sx={{ flex: 1 }}>
//                                     {taskStatusData.map((t, i) => (
//                                         <Box
//                                             key={i}
//                                             sx={{
//                                                 display: "flex",
//                                                 justifyContent: "space-between",
//                                                 alignItems: "center",
//                                                 mb: 0.5,
//                                             }}
//                                         >
//                                             <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
//                                                 <Box
//                                                     sx={{
//                                                         width: 7,
//                                                         height: 7,
//                                                         borderRadius: "50%",
//                                                         bgcolor: t.color,
//                                                     }}
//                                                 />
//                                                 <Typography sx={{ fontSize: 9, color: C.text2 }}>
//                                                     {t.name}
//                                                 </Typography>
//                                             </Box>

//                                             <Typography
//                                                 sx={{
//                                                     fontSize: 9,
//                                                     fontWeight: 700,
//                                                     color: C.text1,
//                                                 }}
//                                             >
//                                                 {t.value}
//                                             </Typography>
//                                         </Box>
//                                     ))}
//                                 </Box>
//                             </Box>
//                         </Section>
//                     </Grid>

//                     {/* Team Velocity */}
//                     <Grid item xs={12} md={4}>
//                         <Section title="Team Velocity" right={<Badge label="Stories/Sprint" color={C.orange} />}>
//                             <ResponsiveContainer width="100%" height={150}>
//                                 <BarChart data={velocityData} margin={{ top: 4, right: 4, bottom: 4, left: -20 }}>
//                                     <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
//                                     <XAxis dataKey="sprint" tick={{ fontSize: 8 }} />
//                                     <YAxis tick={{ fontSize: 8 }} />
//                                     <Tooltip contentStyle={{ fontSize: 10 }} />
//                                     <Bar dataKey="planned" fill={C.todo} radius={[2, 2, 0, 0]} barSize={10} name="Planned" />
//                                     <Bar dataKey="actual" fill={C.orange} radius={[2, 2, 0, 0]} barSize={10} name="Actual" />
//                                 </BarChart>
//                             </ResponsiveContainer>
//                             <Box sx={{ display: "flex", gap: 1.5, mt: 0.5 }}>
//                                 {[["Planned", C.todo], ["Actual", C.orange]].map(([l, c]) => (
//                                     <Box key={l} sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
//                                         <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: c }} />
//                                         <Typography sx={{ fontSize: 9, color: C.text2 }}>{l}</Typography>
//                                     </Box>
//                                 ))}
//                             </Box>
//                         </Section>
//                     </Grid>
//                 </Grid>

//                 {/* ── FLOW & PROGRESS HEADER ── */}
//                 <Typography sx={{ fontSize: 10, fontWeight: 700, color: C.text2, textTransform: "uppercase", letterSpacing: 0.8, mb: 1 }}>
//                     Flow & Progress
//                 </Typography>

//                 {/* ── ROW 4: CFD + Sprint Completion ── */}
//                 <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
//                     {/* Cumulative Flow Diagram */}
//                     <Grid item xs={12} md={6}>
//                         <Section title="Cumulative Flow Diagram" right={<Badge label="Stories over time" color={C.teal} />}>
//                             <ResponsiveContainer width="100%" height={160}>
//                                 <AreaChart data={cfdData} margin={{ top: 4, right: 8, bottom: 4, left: -10 }}>
//                                     <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
//                                     <XAxis dataKey="date" tick={{ fontSize: 8 }} />
//                                     <YAxis tick={{ fontSize: 8 }} />
//                                     <Tooltip contentStyle={{ fontSize: 10 }} />
//                                     <Area type="monotone" dataKey="backlog" stackId="1" stroke={C.orange} fill={C.orange + "55"} name="Backlog" />
//                                     <Area type="monotone" dataKey="inProgress" stackId="1" stroke={C.todo} fill={C.todo + "66"} name="In Progress" />
//                                     <Area type="monotone" dataKey="done" stackId="1" stroke={C.teal} fill={C.teal + "88"} name="Done" />
//                                 </AreaChart>
//                             </ResponsiveContainer>
//                             <Box sx={{ display: "flex", gap: 1.5, mt: 0.5 }}>
//                                 {[["Backlog", C.orange], ["In Progress", C.todo], ["Done", C.teal]].map(([l, c]) => (
//                                     <Box key={l} sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
//                                         <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: c }} />
//                                         <Typography sx={{ fontSize: 9, color: C.text2 }}>{l}</Typography>
//                                     </Box>
//                                 ))}
//                             </Box>
//                         </Section>
//                     </Grid>

//                     {/* Sprint Completion */}
//                     <Grid item xs={12} md={6}>
//                         <Section title="Sprint Completion" right={<Badge label="Progress %" color={C.blue} />}>
//                             <Box sx={{ mt: 1 }}>
//                                 {sprintCompletion.map((item, i) => (
//                                     <Box key={i} sx={{ mb: 2 }}>
//                                         <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
//                                             <Typography sx={{ fontSize: 10, color: C.text1, fontWeight: 600 }}>{item.label}</Typography>
//                                             <Typography sx={{ fontSize: 10, color: C.text1, fontWeight: 700 }}>{item.completed}%</Typography>
//                                         </Box>
//                                         <Box sx={{ position: "relative", height: 20, bgcolor: "#f0f0f0", borderRadius: 1, overflow: "hidden" }}>
//                                             <Box sx={{
//                                                 width: `${item.completed}%`, height: "100%",
//                                                 bgcolor: item.completed === 100 ? C.orange : C.blue,
//                                                 borderRadius: 1, display: "flex", alignItems: "center", justifyContent: "flex-end", pr: 0.8,
//                                                 transition: "width 0.6s ease",
//                                             }}>
//                                                 {item.completed > 10 && (
//                                                     <Typography sx={{ fontSize: 9, color: "#fff", fontWeight: 700 }}>
//                                                         {item.completed}%
//                                                     </Typography>
//                                                 )}
//                                             </Box>
//                                             {item.remaining > 0 && (
//                                                 <Box sx={{
//                                                     position: "absolute", right: 6, top: "50%",
//                                                     transform: "translateY(-50%)",
//                                                 }}>
//                                                     <Typography sx={{ fontSize: 9, color: C.text2, fontWeight: 600 }}>
//                                                         {item.remaining}%
//                                                     </Typography>
//                                                 </Box>
//                                             )}
//                                         </Box>
//                                         <Box sx={{ display: "flex", gap: 1.5, mt: 0.4 }}>
//                                             <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
//                                                 <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: item.completed === 100 ? C.orange : C.blue }} />
//                                                 <Typography sx={{ fontSize: 8, color: C.text2 }}>Completed</Typography>
//                                             </Box>
//                                             <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
//                                                 <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#e0e0e0" }} />
//                                                 <Typography sx={{ fontSize: 8, color: C.text2 }}>Remaining</Typography>
//                                             </Box>
//                                         </Box>
//                                     </Box>
//                                 ))}
//                             </Box>
//                         </Section>
//                     </Grid>
//                 </Grid>

//                 {/* ── QUALITY & EFFICIENCY HEADER ── */}
//                 <Typography sx={{ fontSize: 10, fontWeight: 700, color: C.text2, textTransform: "uppercase", letterSpacing: 0.8, mb: 1 }}>
//                     Quality & Efficiency
//                 </Typography>

//                 {/* ── ROW 5: Bug Trend + Lead & Cycle Time ── */}
//                 <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
//                     {/* Bug/Defect Trend */}
//                     <Grid item xs={12} md={6}>
//                         <Section
//                             title="Bug / Defect Trend"
//                             right={
//                                 <Box sx={{ display: "flex", gap: 1 }}>
//                                     <Badge label="Opened vs Closed per sprint" color={C.red} />
//                                 </Box>
//                             }
//                         >
//                             <ResponsiveContainer width="100%" height={160}>
//                                 <LineChart data={bugTrendData} margin={{ top: 4, right: 8, bottom: 4, left: -10 }}>
//                                     <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
//                                     <XAxis dataKey="date" tick={{ fontSize: 8 }} />
//                                     <YAxis tick={{ fontSize: 8 }} />
//                                     <Tooltip contentStyle={{ fontSize: 10 }} />
//                                     <Line type="monotone" dataKey="opened" stroke={C.red} dot={{ r: 3 }} strokeWidth={2} name="Opened" />
//                                     <Line type="monotone" dataKey="closed" stroke={C.green} dot={{ r: 3 }} strokeWidth={2} name="Closed" />
//                                 </LineChart>
//                             </ResponsiveContainer>
//                             <Box sx={{ display: "flex", gap: 1.5, mt: 0.5 }}>
//                                 {[["Opened", C.red], ["Closed", C.green]].map(([l, c]) => (
//                                     <Box key={l} sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
//                                         <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: c }} />
//                                         <Typography sx={{ fontSize: 9, color: C.text2 }}>{l}</Typography>
//                                     </Box>
//                                 ))}
//                             </Box>
//                         </Section>
//                     </Grid>

//                     {/* Lead & Cycle Time */}
//                     <Grid item xs={12} md={6}>
//                         <Section title="Lead & Cycle Time" right={<Badge label="avg days/task" color={C.blue} />}>
//                             <Box sx={{ display: "flex", justifyContent: "center", gap: 4, pt: 1 }}>
//                                 <Gauge value={4.2} max={10} color={C.orange} label="Lead Time" sub="Backlog → Done" />
//                                 <Box sx={{ width: 1, bgcolor: C.border, my: 2 }} />
//                                 <Gauge value={2.1} max={10} color={C.blue} label="Cycle Time" sub="In Progress → Done" />
//                             </Box>
//                         </Section>
//                     </Grid>
//                 </Grid>

//                 {/* ── TEAM WORKLOAD HEADER ── */}
//                 <Typography sx={{ fontSize: 10, fontWeight: 700, color: C.text2, textTransform: "uppercase", letterSpacing: 0.8, mb: 1 }}>
//                     Team Workload
//                 </Typography>

//                 {/* ── ROW 6: Member Workload Distribution ── */}
//                 <Section title="Member Workload Distribution" right={<Badge label="Tasks per team member" color={C.purple} />}>
//                     <Box sx={{ mt: 1 }}>
//                         {memberWorkload.map((m, i) => {
//                             const total = m.backlog + m.inProgress + m.done + m.blocked;
//                             return (
//                                 <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
//                                     <Avatar sx={{ width: 26, height: 26, fontSize: 10, bgcolor: [C.blue, C.orange, C.green, C.red, C.purple, C.teal][i % 6], fontWeight: 700 }}>
//                                         {m.name[0]}
//                                     </Avatar>
//                                     <Typography sx={{ fontSize: 10, fontWeight: 600, color: C.text1, width: 42 }}>{m.name}</Typography>
//                                     <Box sx={{ flex: 1, display: "flex", height: 18, borderRadius: 1, overflow: "hidden" }}>
//                                         {[
//                                             { val: m.backlog, color: C.blue },
//                                             { val: m.inProgress, color: C.orange },
//                                             { val: m.done, color: C.green },
//                                             { val: m.blocked, color: C.red },
//                                         ].map((seg, si) => {
//                                             const w = (seg.val / total) * 100;
//                                             return w > 0 ? (
//                                                 <Box key={si} sx={{
//                                                     width: `${w}%`, bgcolor: seg.color,
//                                                     display: "flex", alignItems: "center", justifyContent: "center",
//                                                 }}>
//                                                     {w > 8 && (
//                                                         <Typography sx={{ fontSize: 8, color: "#fff", fontWeight: 700 }}>{seg.val}</Typography>
//                                                     )}
//                                                 </Box>
//                                             ) : null;
//                                         })}
//                                     </Box>
//                                     <Typography sx={{ fontSize: 9, fontWeight: 700, color: C.text1, width: 20 }}>{total}</Typography>
//                                 </Box>
//                             );
//                         })}
//                     </Box>
//                     <Box sx={{ display: "flex", gap: 2, mt: 1, pt: 1, borderTop: `1px solid ${C.border}` }}>
//                         {[["Backlog", C.blue], ["In Progress", C.orange], ["Done", C.green], ["Blocked", C.red]].map(([l, c]) => (
//                             <Box key={l} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
//                                 <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: c }} />
//                                 <Typography sx={{ fontSize: 9, color: C.text2 }}>{l}</Typography>
//                             </Box>
//                         ))}
//                     </Box>
//                 </Section>

//             </Box>
//         </Box>
//     );
// }

// const navBtnStyle = {
//     width: 22,
//     height: 22,
//     borderRadius: 6,
//     backgroundColor: "rgba(255,255,255,0.2)",
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "center",
//     color: "#fff",
//     fontSize: 12,
//     cursor: "pointer",
// };